import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {Box, CircularProgress, Stack, Typography} from "@mui/material";
import {useDeleteTaskMutation, useGetTasksListQuery, useUpdateTaskMutation} from "../../../../services/tasks";
import RowActionsMenu, {RowMenuState} from "./RowActionsMenu";
import type {TaskDto, UpdateTaskDto} from "../../../../types/task";
import {toast} from "react-toastify";
import CampaignCard from "./CampaingCard";
import CampaignsHeader from "./CampaignsHeader";
import TaskDialog, {TaskDialogValues} from "../TaskDialog";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {useLazyGetTaskQuery} from "../../../../services/tasks";
import {useGetTemplatesQuery} from "../../../../services/templates";
import {useGetEmailListsQuery} from "../../../../services/emailListApi";
import dayjs from "dayjs";

// ---------- main ----------
export default function EmailCampaignsCards() {
  const {data: allRows = [], isLoading, isError, error} = useGetTasksListQuery();
  const [deleteTaskApi] = useDeleteTaskMutation();
  const [updateTaskApi] = useUpdateTaskMutation();

  const [search, setSearch] = useState("");
  const [menu, setMenu] = useState<RowMenuState>({anchorEl: null, row: null});
  const [open, setOpen] = useState(false);
  const [getTaskApi,{data:taskData }] = useLazyGetTaskQuery();

  // todo:// useLazy load or use RTK query cache ?
  const { data: templates = [] } = useGetTemplatesQuery();
  const { data: destinationEmailList = [] } = useGetEmailListsQuery();
  const destinationOptions = destinationEmailList.map((x) => ({
    id: x.id,
    label: x.title
  }));
  const templateOptions = templates.map((t) => ({
    id: t.id,
    label: t.name
  }));

  const [initialValues, setInitialValues] = useState<Partial<TaskDialogValues>>({});

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...allRows]
      .filter((r) => q === "" || r.subject.toLowerCase().includes(q))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [allRows, search]);

  const openMenu = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, row: TaskDto) => setMenu({anchorEl: e.currentTarget, row}),
    []
  );

  const deleteTask = useCallback(
    async (id: string) => {
      try {
        await deleteTaskApi(id).unwrap();
      } catch (e) {
        const err = e as { data?: any; message?: string };
        const msg = err?.data?.error ?? err?.message ?? "Unknown error";
        toast.error(`Error deleting task: ${msg}`);
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },
    [deleteTaskApi]
  );

  const closeMenu = useCallback(
    async (action?: string) => {
      if (action && menu.row) {
        switch (action) {
          case "delete":
            await deleteTask(menu.row.id);
            break;
          case "edit":
            const task = await getTaskApi(menu.row.id).unwrap();
            console.log("task:", task);
            setInitialValues({
              id: task.id,
              subject: task.title,
              hiddenPreview: task.hiddenText,
              scheduleDate: task.planedDate ? dayjs(task.planedDate) : null,
              destinationListId: task.targetListId,
              templateId: task.templateId
            });
            setOpen(true);
            // load data by id
            //
            break;
          default:
            // eslint-disable-next-line no-console
            console.log(`Unhandled action: ${action}`);
        }
      }
      setMenu({anchorEl: null, row: null});
    },
    [deleteTask, menu.row]
  );

  // ---------- render states ----------
  if (isLoading) {
    return (
      <Box sx={{p: {xs: 2, md: 3}, display: "flex", justifyContent: "center"}}>
        <CircularProgress aria-label="Loading campaigns"/>
      </Box>
    );
  }

  if (isError) {
    const msg = (error as any)?.data?.error ?? (error as any)?.error ?? "Failed to load campaigns";
    return (
      <Box sx={{p: {xs: 2, md: 3}}}>
        <Typography color="error" role="alert">{msg}</Typography>
      </Box>
    );
  }

  const closeHandler = () => {
    setOpen(false);
  }

  const submitHandler = async (values: any) => {
    console.log("update values", values);
    console.log("taskData", taskData);
    if (!taskData) return;
    const id = taskData.id;
    const payload: UpdateTaskDto = {
      id,
      title: values.subject,
      hiddenText: values.hiddenPreview,
      planedDate: values.scheduleDate!.toDate().toISOString(),
      targetListId: values.destinationListId,
      templateId: values.templateId
    }
    try {
      console.log("update task api call", payload);
      await updateTaskApi(payload).unwrap();
      setOpen(false);
    } catch (e) {
      toast.error("Failed to update campaign, please try again.");
    }
  }

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TaskDialog open={open} mode="edit" onClose={closeHandler} onSubmit={submitHandler} destinationOptions={destinationOptions}
                    templateOptions={templateOptions} initialValues={initialValues}/>
      </LocalizationProvider>
      <Box sx={{p: {xs: 2, md: 3}}}>
        <CampaignsHeader search={search} onSearch={setSearch}/>
        <Stack spacing={2}>
          {filtered.map((row) => (
            <CampaignCard key={row.id} row={row} onOpenMenu={openMenu}/>
          ))}
          {filtered.length === 0 && (
            <Typography color="text.secondary">No campaigns found.</Typography>
          )}
        </Stack>
        <RowActionsMenu state={menu} onClose={closeMenu}/>
      </Box>
    </>
  );
}
